import React, { useEffect, useMemo, useRef } from 'react';
import './WheelDatePicker.css';

export interface WheelDateValue {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

export interface WheelDatePickerProps {
  value: WheelDateValue;
  onChange: (next: WheelDateValue) => void;
  minYear?: number;
  maxYear?: number;
  /** BCP 47 tag for full month names (e.g. `ja-JP`, `en-US`). */
  locale?: string;
}

type WheelItem<T extends string | number> = { value: T; label: string };

/** Compact wheel: smaller rows + shorter viewport (~5 rows, edges fade via CSS mask). */
const ITEM_HEIGHT = 32;
const WHEEL_HEIGHT = 160;
const SPACER_ROWS = 2; // matches the sample: 2 rows above & below

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function Wheel<T extends string | number>({
  items,
  selectedValue,
  ariaLabel,
  onChange,
}: {
  items: WheelItem<T>[];
  selectedValue: T;
  ariaLabel: string;
  onChange: (value: T) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const scrollEndTimerRef = useRef<number | null>(null);
  const isProgrammaticRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const apply3DEffect = () => {
    const el = ref.current;
    if (!el) return;
    const centerY = el.scrollTop + el.clientHeight / 2;
    const nodes = Array.from(el.querySelectorAll<HTMLElement>('.wheel-item'));
    const maxDist = ITEM_HEIGHT * 4;
    for (const node of nodes) {
      const itemCenter = node.offsetTop + node.offsetHeight / 2;
      const dist = itemCenter - centerY;
      const t = clamp(Math.abs(dist) / maxDist, 0, 1);
      const sign = dist === 0 ? 0 : dist > 0 ? 1 : -1;
      const angle = sign * (t * 40); // degrees
      const z = 120 * (1 - t); // px
      const opacity = 0.25 + (1 - t) * 0.75;
      node.style.transform = `translateZ(${z}px) rotateX(${angle}deg)`;
      node.style.opacity = String(opacity);
    }
  };

  const selectedIndex = useMemo(() => {
    const idx = items.findIndex((it) => it.value === selectedValue);
    return idx >= 0 ? idx : 0;
  }, [items, selectedValue]);

  // Keep scroll position in sync with the controlled selected value.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    isProgrammaticRef.current = true;
    el.scrollTop = selectedIndex * ITEM_HEIGHT;
    window.setTimeout(() => {
      isProgrammaticRef.current = false;
    }, 80);
    // After syncing, refresh 3D transforms.
    const id = window.requestAnimationFrame(apply3DEffect);
    return () => window.cancelAnimationFrame(id);
  }, [selectedIndex]);

  useEffect(() => {
    return () => {
      if (scrollEndTimerRef.current != null) window.clearTimeout(scrollEndTimerRef.current);
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleScroll = () => {
    const el = ref.current;
    if (!el) return;
    if (scrollEndTimerRef.current != null) window.clearTimeout(scrollEndTimerRef.current);
    const index = clamp(Math.round(el.scrollTop / ITEM_HEIGHT), 0, items.length - 1);
    if (!isProgrammaticRef.current) onChange(items[index].value);

    if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(apply3DEffect);

    // Snap to exact row after the scroll momentum ends.
    scrollEndTimerRef.current = window.setTimeout(() => {
      const c = ref.current;
      if (!c) return;
      const idx2 = clamp(Math.round(c.scrollTop / ITEM_HEIGHT), 0, items.length - 1);
      isProgrammaticRef.current = true;
      c.scrollTop = idx2 * ITEM_HEIGHT;
      window.setTimeout(() => {
        isProgrammaticRef.current = false;
      }, 80);
      apply3DEffect();
    }, 140);
  };

  return (
    <div className="wheel-container" ref={ref} onScroll={handleScroll} aria-label={ariaLabel}>
      <div style={{ height: ITEM_HEIGHT * SPACER_ROWS }} aria-hidden />
      {items.map((item, i) => (
        <div key={`${String(item.value)}-${i}`} className={`wheel-item ${i === selectedIndex ? 'active' : ''}`}>
          {item.label}
        </div>
      ))}
      <div style={{ height: ITEM_HEIGHT * SPACER_ROWS }} aria-hidden />
    </div>
  );
}

const WheelDatePicker: React.FC<WheelDatePickerProps> = ({ value, onChange, minYear = 1920, maxYear, locale }) => {
  const currentYear = new Date().getFullYear();
  const upperYear = maxYear ?? currentYear;
  const effectiveLocale = locale ?? (typeof navigator !== 'undefined' ? navigator.language : 'ja-JP');

  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(effectiveLocale, { month: 'long' }),
    [effectiveLocale]
  );

  const yearItems = useMemo<WheelItem<number>[]>(() => {
    const list: WheelItem<number>[] = [];
    for (let y = upperYear; y >= minYear; y--) list.push({ value: y, label: String(y) });
    return list;
  }, [minYear, upperYear]);

  const monthItems = useMemo<WheelItem<number>[]>(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      return { value: m, label: monthFormatter.format(new Date(2000, m - 1, 15)) };
    });
  }, [monthFormatter]);

  const maxDay = daysInMonth(value.year, value.month);
  const safeDay = clamp(value.day, 1, maxDay);

  const dayItems = useMemo<WheelItem<number>[]>(() => {
    return Array.from({ length: maxDay }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
  }, [maxDay]);

  // Clamp day if month/year change reduced the max.
  useEffect(() => {
    if (safeDay !== value.day) onChange({ ...value, day: safeDay });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDay]);

  return (
    <div className="picker-wrapper" style={{ height: WHEEL_HEIGHT }}>
      <div className="highlight" aria-hidden />
      <Wheel
        items={yearItems}
        selectedValue={value.year}
        ariaLabel="Year"
        onChange={(year) => {
          const nextMax = daysInMonth(year, value.month);
          onChange({ year, month: value.month, day: clamp(value.day, 1, nextMax) });
        }}
      />
      <Wheel
        items={monthItems}
        selectedValue={value.month}
        ariaLabel="Month"
        onChange={(month) => {
          const nextMax = daysInMonth(value.year, month);
          onChange({ year: value.year, month, day: clamp(value.day, 1, nextMax) });
        }}
      />
      <Wheel
        items={dayItems}
        selectedValue={safeDay}
        ariaLabel="Day"
        onChange={(day) => onChange({ ...value, day })}
      />
    </div>
  );
};

export default WheelDatePicker;
